import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styles from './FormationMap.module.scss';
import PlaceholderJugador from 'containers/Mundial2026/assets/placeholders/Jugador.png';

const getLastName = name => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1] || '';
};

const ArrowDown = ({ assets }) => (
  <img
    src={assets?.eventodeportivo_icono_sale}
    alt=""
    width="12"
    height="12"
  />
);

const Goal = ({ assets }) => (
  <img
    src={assets?.eventodeportivo_icono_gol_2}
    alt=""
    width="12"
    height="12"
  />
);

const ArrowUp = ({ assets }) => (
  <img
    src={assets?.eventodeportivo_icono_entra}
    alt=""
    width="12"
    height="12"
  />
);

const YellowCard = ({ assets }) => (
  <img
    src={assets?.eventodeportivo_icono_amarilla_2}
    alt=""
    width="12"
    height="12"
  />
);

const RedCard = ({ assets }) => (
  <img
    src={assets?.eventodeportivo_icono_roja_2}
    alt=""
    width="12"
    height="12"
  />
);

const YellowRedCard = ({ assets }) => (
  <img
    src={assets?.eventodeportivo_icono_amarillaroja_1}
    alt=""
    width="12"
    height="12"
  />
);

const Pitch = ({ children }) => {
  const suffix = useRef(
    Math.random()
      .toString(36)
      .slice(2, 9),
  ).current;
  const tabletOuterId = `pitch-outer-t-${suffix}`;
  const tabletInnerId = `pitch-inner-t-${suffix}`;
  const desktopOuterId = `pitch-outer-d-${suffix}`;
  const desktopInnerId = `pitch-inner-d-${suffix}`;

  return (
    <div className={styles['pitch']}>
      {/* Mobile/tablet field border — 202x88 (penalty area + 108x48 goal area inside) */}
      <svg
        className={`${styles['pitch__field-border']} ${
          styles['pitch__field-border--tablet']
        }`}
        viewBox="0 0 202 88"
        preserveAspectRatio="xMidYMin meet"
        aria-hidden="true"
      >
        <g opacity="0.5">
          <mask id={tabletOuterId} fill="white">
            <path d="M0 0H202V88H0V0Z" />
          </mask>
          <path
            d="M202 88V89H203V88H202ZM0 88H-1V89H0V88ZM202 0H201V88H202H203V0H202ZM202 88V87H0V88V89H202V88ZM0 88H1V0H0H-1V88H0Z"
            fill="#B5B5FF"
            fillOpacity="0.7"
            mask={`url(#${tabletOuterId})`}
          />
        </g>
        <g opacity="0.5">
          <mask id={tabletInnerId} fill="white">
            <path d="M47 0H155V48H47V0Z" />
          </mask>
          <path
            d="M155 48V49H156V48H155ZM47 48H46V49H47V48ZM155 0H154V48H155H156V0H155ZM155 48V47H47V48V49H155V48ZM47 48H48V0H47H46V48H47Z"
            fill="#B5B5FF"
            fillOpacity="0.7"
            mask={`url(#${tabletInnerId})`}
          />
        </g>
      </svg>

      {/* Desktop field border — 174x78 (penalty area + 62x16 goal area inside) */}
      <svg
        className={`${styles['pitch__field-border']} ${
          styles['pitch__field-border--desktop']
        }`}
        viewBox="0 0 174 78"
        preserveAspectRatio="xMidYMin meet"
        aria-hidden="true"
      >
        <g opacity="0.5">
          <mask id={desktopOuterId} fill="white">
            <path d="M0 0H174V78H0V0Z" />
          </mask>
          <path
            d="M174 78V79H175V78H174ZM0 78H-1V79H0V78ZM174 0H173V78H174H175V0H174ZM174 78V77H0V78V79H174V78ZM0 78H1V0H0H-1V78H0Z"
            fill="#B5B5FF"
            fillOpacity="0.7"
            mask={`url(#${desktopOuterId})`}
          />
        </g>
        <g opacity="0.5">
          <mask id={desktopInnerId} fill="white">
            <path d="M56 0H118V16H56V0Z" />
          </mask>
          <path
            d="M118 16V17H119V16H118ZM56 16H55V17H56V16ZM118 0H117V16H118H119V0H118ZM118 16V15H56V16V17H118V16ZM56 16H57V0H56H55V16H56Z"
            fill="#B5B5FF"
            fillOpacity="0.7"
            mask={`url(#${desktopInnerId})`}
          />
        </g>
      </svg>

      {/* Mobile/tablet center arc — 140x70, stroke 1px (default) */}
      <svg
        className={`${styles['pitch__center-arc']} ${
          styles['pitch__center-arc--tablet']
        }`}
        viewBox="0 0 140 70"
        preserveAspectRatio="xMidYMax meet"
        aria-hidden="true"
      >
        <circle
          opacity="0.5"
          cx="70"
          cy="70"
          r="69.5"
          transform="rotate(90 70 70)"
          stroke="#B5B5FF"
          strokeOpacity="0.7"
          fill="none"
        />
      </svg>

      {/* Desktop center arc — 88x44, stroke 2px, opacity 0.5 */}
      <svg
        className={`${styles['pitch__center-arc']} ${
          styles['pitch__center-arc--desktop']
        }`}
        viewBox="0 0 88 44"
        preserveAspectRatio="xMidYMax meet"
        aria-hidden="true"
      >
        <circle
          opacity="0.5"
          cx="44"
          cy="44"
          r="43"
          transform="rotate(90 44 44)"
          stroke="#B5B5FF"
          strokeOpacity="0.5"
          strokeWidth="2"
          fill="none"
        />
      </svg>

      <div className={styles['pitch__center-line']} aria-hidden="true" />

      <div className={styles['pitch__players']}>{children}</div>
    </div>
  );
};

Pitch.propTypes = {
  children: PropTypes.node,
};

Pitch.defaultProps = {
  children: null,
};

const PitchPlayer = ({ player, primaryColor }) => {
  const assets = useSelector(state => state.asset);
  const lastName = getLastName(player.name);
  const movements = player.movements || [];

  const hasGoal = movements.some(m => m.type === 'GOAL');
  const yellowCard = movements.find(m => m.type === 'YELLOW_CARD');
  const redCard = movements.find(m => m.type === 'RED_CARD');
  const secondYellow = movements.find(m => m.type === 'SECOND_YELLOW_RED_CARD');
  const subOut = movements.find(m => m.type === 'SUBSTITUTION_OUT');
  const subIn = movements.find(m => m.type === 'SUBSTITUTION_IN');

  const positionStyle = {
    left: `${player.formationMap.x}%`,
    top: `${player.formationMap.y}%`,
  };

  const avatarStyle = primaryColor ? { borderColor: primaryColor } : undefined;
  const hasNumber = player.number !== null && player.number !== undefined;

  return (
    <div className={styles['pitch-player']} style={positionStyle}>
      <div className={styles['pitch-player__core']}>
        <div className={styles['pitch-player__avatar']} style={avatarStyle}>
          <img
            src={player.image || PlaceholderJugador}
            alt={lastName}
            className={styles['pitch-player__avatar-image']}
            onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = PlaceholderJugador;
            }}
          />
        </div>

        <div className={styles['pitch-player__cards-slot']}>
          {yellowCard && <YellowCard assets={assets} />}
          {secondYellow && <YellowRedCard assets={assets} />}
          {redCard && <RedCard assets={assets} />}
        </div>

        <div className={styles['pitch-player__sub-slot']}>
          {subOut && (
            <span
              className={`${styles['pitch-player__sub']} ${
                styles['pitch-player__sub--out']
              }`}
            >
              <ArrowDown assets={assets} />
            </span>
          )}
          {subIn && (
            <span
              className={`${styles['pitch-player__sub']} ${
                styles['pitch-player__sub--in']
              }`}
            >
              <ArrowUp assets={assets} />
            </span>
          )}
        </div>

        <div className={styles['pitch-player__goal-slot']}>
          {hasGoal && <Goal assets={assets} />}
          {hasNumber && (
            <span className={styles['pitch-player__number']}>
              {player.number}
            </span>
          )}
        </div>
      </div>

      {lastName && <p className={styles['pitch-player__name']}>{lastName}</p>}
    </div>
  );
};

PitchPlayer.propTypes = {
  player: PropTypes.shape({
    playerId: PropTypes.string,
    name: PropTypes.string,
    image: PropTypes.string,
    number: PropTypes.number,
    formationMap: PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    }).isRequired,
    movements: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        minute: PropTypes.number,
      }),
    ),
  }).isRequired,
  primaryColor: PropTypes.string,
};

PitchPlayer.defaultProps = {
  primaryColor: null,
};

const FormationMap = ({
  goalkeeper,
  defenders,
  midfielders,
  forwards,
  formation,
  primaryColor,
}) => {
  const allPlayers = [
    ...(goalkeeper || []),
    ...(defenders || []),
    ...(midfielders || []),
    ...(forwards || []),
  ].filter(p => p && p.showInFormationMap && p.formationMap);

  return (
    <div className={styles['formation-map']}>
      <Pitch>
        {allPlayers.map(player => (
          <PitchPlayer
            key={player.playerId}
            player={player}
            primaryColor={primaryColor}
          />
        ))}
      </Pitch>

      {formation && (
        <p className={styles['formation-map__formation']}>{formation}</p>
      )}
    </div>
  );
};

FormationMap.propTypes = {
  goalkeeper: PropTypes.array,
  defenders: PropTypes.array,
  midfielders: PropTypes.array,
  forwards: PropTypes.array,
  formation: PropTypes.string,
  primaryColor: PropTypes.string,
};

FormationMap.defaultProps = {
  goalkeeper: [],
  defenders: [],
  midfielders: [],
  forwards: [],
  formation: null,
  primaryColor: null,
};

export default FormationMap;
